import * as THREE from 'three';
import { Spin } from '../animated';
import { FUTURE, INDUSTRIAL, MODERN, translucent } from '../materials';
import { gableRoof, hipRoof, type ModelProps, Pole, pyramid, Smoke } from '../parts';
import { WALL_CORNERS, WALL_RADIUS, WALL_SEGMENTS } from './parts';

/**
 * The later-era city stages (L5 Industrial, L6 Modern, L7 Future). Like the
 * classical ones they stay inside the hex (`WALL_RADIUS`), keep the tallest
 * parts slim and at the back (the camera looks from +z), and are scaled by
 * the city's CITY_SCALE.
 */

const { BRICK, DARK_BRICK, IRON, SLATE, CLOCK, COBBLE } = INDUSTRIAL;
const { CONCRETE, DARK_CONCRETE, GLASS, ASPHALT, ROAD_LINE, STEEL, WINDOW, WHITE, RED_LIGHT } =
  MODERN;
const { HULL, PANEL, DARK_PANEL, DOME, GARDEN } = FUTURE;

const G = {
  // Ground: a thin hex slab just inside the wall.
  plaza: new THREE.CylinderGeometry(WALL_RADIUS - 0.02, WALL_RADIUS - 0.02, 0.02, 6),
  rail: new THREE.BoxGeometry(1, 0.012, 0.012),
  picket: new THREE.BoxGeometry(0.01, 0.1, 0.01),
  pillar: new THREE.BoxGeometry(0.06, 0.14, 0.06),
  pillarCap: pyramid(0.05, 0.04),
  // Industrial
  hall: new THREE.BoxGeometry(0.36, 0.2, 0.24),
  hallRoof: hipRoof(0.4, 0.28, 0.12, 0.2),
  clockTower: new THREE.BoxGeometry(0.1, 0.4, 0.1),
  clockFace: new THREE.CylinderGeometry(0.035, 0.035, 0.01, 12).rotateX(Math.PI / 2),
  clockCap: pyramid(0.09, 0.14),
  chimney: new THREE.CylinderGeometry(0.028, 0.036, 0.46, 6),
  works: new THREE.BoxGeometry(0.2, 0.14, 0.16),
  worksRoof: gableRoof(0.1, 0.22),
  row: new THREE.BoxGeometry(0.28, 0.11, 0.11),
  rowRoof: gableRoof(0.075, 0.3),
  rowStack: new THREE.BoxGeometry(0.025, 0.05, 0.025),
  // Modern
  lane: new THREE.BoxGeometry(1, 0.006, 0.07),
  laneLine: new THREE.BoxGeometry(1, 0.008, 0.008),
  kerb: new THREE.BoxGeometry(1, 0.04, 0.03),
  tower: new THREE.BoxGeometry(1, 1, 1),
  strip: new THREE.BoxGeometry(1, 1, 1),
  helipad: new THREE.CylinderGeometry(0.1, 0.1, 0.012, 16),
  helipadRing: new THREE.TorusGeometry(0.085, 0.008, 4, 20).rotateX(Math.PI / 2),
  hBar: new THREE.BoxGeometry(0.014, 0.004, 0.07),
  hCross: new THREE.BoxGeometry(0.04, 0.004, 0.014),
  beacon: new THREE.SphereGeometry(0.018, 6, 4),
  // Future
  wallPanel: new THREE.BoxGeometry(1, 0.16, 0.012),
  pylon: new THREE.CylinderGeometry(0.022, 0.03, 0.2, 6),
  pylonTip: new THREE.OctahedronGeometry(0.03, 0),
  spire: new THREE.CylinderGeometry(0.018, 0.075, 1, 6),
  collar: new THREE.CylinderGeometry(0.13, 0.15, 0.08, 6),
  energyRing: new THREE.TorusGeometry(0.16, 0.012, 6, 32),
  spireTip: new THREE.OctahedronGeometry(0.035, 0),
  dome: new THREE.SphereGeometry(1, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2),
  domeFloor: new THREE.CylinderGeometry(1, 1, 0.02, 16),
  domeRim: new THREE.TorusGeometry(1, 0.06, 4, 24).rotateX(Math.PI / 2),
  needle: new THREE.CylinderGeometry(0.02, 0.035, 1, 6),
  lightPad: new THREE.CylinderGeometry(0.06, 0.06, 0.012, 6),
};

/** Place a piece `r` from the centre at angle `a` (radians from +x, like the houses). */
function ring(r: number, a: number): [number, number, number] {
  return [Math.cos(a) * r, 0, Math.sin(a) * r];
}

// ---------------------------------------------------------------- Industrial

const PICKET_GAP = 0.06;
// Picket offsets along one side, between (not on) the corner pillars.
const PICKET_COUNT = Math.round(WALL_RADIUS / PICKET_GAP) - 1;
const PICKETS = Array.from(
  { length: PICKET_COUNT },
  (_, i) => (i + 1 - (PICKET_COUNT + 1) / 2) * PICKET_GAP
);

/** Iron railings along every wall side, on brick pillars at the corners. */
function IronFence({ accent }: { accent: THREE.Material }) {
  return (
    <>
      {WALL_SEGMENTS.map((seg) => (
        <group key={seg.yaw} position={[seg.x, 0, seg.z]} rotation={[0, seg.yaw, 0]}>
          {[0.04, 0.09].map((y) => (
            <mesh
              key={y}
              geometry={G.rail}
              material={IRON}
              position={[0, y, 0]}
              scale={[WALL_RADIUS, 1, 1]}
            />
          ))}
          {PICKETS.map((x) => (
            <mesh key={x} geometry={G.picket} material={IRON} position={[x, 0.05, 0]} />
          ))}
        </group>
      ))}
      {WALL_CORNERS.map((c) => (
        <group key={c.a} position={[c.x, 0, c.z]}>
          <mesh geometry={G.pillar} material={BRICK} position={[0, 0.07, 0]} />
          <mesh geometry={G.pillarCap} material={accent} position={[0, 0.16, 0]} />
        </group>
      ))}
    </>
  );
}

// Terraced rows round the front and sides, laid along the ring.
const ROW_ANGLES = [0.25, 1.05, 2.1, 2.9];
const ROW_RING = 0.44;
// Two works sheds at the back, each with a chimney behind it.
const WORKS = [
  { a: 3.85, chimney: [-0.34, 0, -0.38] as [number, number, number] },
  { a: 5.55, chimney: [0.36, 0, -0.34] as [number, number, number] },
];
const CHIMNEY_HEIGHT = 0.46;
const CHIMNEY_TOPS = WORKS.map(
  (w) => [w.chimney[0], CHIMNEY_HEIGHT + 0.02, w.chimney[2]] as [number, number, number]
);

/**
 * L5 Industrial: a brick town hall with a team hip roof and a clock tower,
 * rows of brick terraces under iron roofs, two works sheds with smoking
 * chimneys, all inside iron railings on brick pillars.
 */
export function Industrial({ accent }: ModelProps) {
  return (
    <>
      <mesh geometry={G.plaza} material={COBBLE} position={[0, 0.01, 0]} />
      <IronFence accent={accent} />
      <group position={[0, 0, -0.06]}>
        <mesh geometry={G.hall} material={BRICK} position={[0, 0.1, 0]} />
        <mesh geometry={G.hallRoof} material={accent} position={[0, 0.2, 0]} />
        <group position={[0, 0, 0.06]}>
          <mesh geometry={G.clockTower} material={DARK_BRICK} position={[0, 0.4, 0]} />
          <mesh geometry={G.clockFace} material={CLOCK} position={[0, 0.52, 0.052]} />
          <mesh geometry={G.clockCap} material={SLATE} position={[0, 0.67, 0]} />
          <Pole length={0.12} position={[0, 0.79, 0]} material={IRON} />
        </group>
      </group>
      {ROW_ANGLES.map((a) => (
        <group key={a} position={ring(ROW_RING, a)} rotation={[0, Math.PI / 2 - a, 0]}>
          <mesh geometry={G.row} material={BRICK} position={[0, 0.055, 0]} />
          <mesh geometry={G.rowRoof} material={IRON} position={[0, 0.145, 0]} />
          {[-0.09, 0.03].map((x) => (
            <mesh key={x} geometry={G.rowStack} material={DARK_BRICK} position={[x, 0.2, -0.02]} />
          ))}
        </group>
      ))}
      {WORKS.map((w) => (
        <group key={w.a}>
          <group position={ring(0.4, w.a)} rotation={[0, Math.PI / 2 - w.a, 0]}>
            <mesh geometry={G.works} material={DARK_BRICK} position={[0, 0.07, 0]} />
            <mesh geometry={G.worksRoof} material={SLATE} position={[0, 0.19, 0]} />
          </group>
          <mesh
            geometry={G.chimney}
            material={DARK_BRICK}
            position={[w.chimney[0], CHIMNEY_HEIGHT / 2, w.chimney[2]]}
          />
        </group>
      ))}
      <Smoke stacks={CHIMNEY_TOPS} />
    </>
  );
}

// ------------------------------------------------------------------ Modern

type Tower = {
  x: number;
  z: number;
  w: number;
  h: number;
  body: THREE.Material;
  /** Heights of the window strips (in `glow` for glass towers, lit windows otherwise). */
  strips: number[];
};

const TOWERS: readonly Tower[] = [
  // The main glass tower, centre back, carrying the radio mast.
  { x: 0, z: -0.14, w: 0.2, h: 0.74, body: GLASS, strips: [0.16, 0.3, 0.44, 0.58, 0.7] },
  { x: -0.3, z: -0.24, w: 0.16, h: 0.5, body: CONCRETE, strips: [0.14, 0.26, 0.38] },
  { x: 0.3, z: -0.26, w: 0.15, h: 0.42, body: GLASS, strips: [0.12, 0.24, 0.36] },
  // Low blocks at the sides and front.
  { x: -0.4, z: 0.16, w: 0.16, h: 0.18, body: CONCRETE, strips: [0.1] },
  { x: -0.12, z: 0.36, w: 0.15, h: 0.14, body: DARK_CONCRETE, strips: [0.08] },
];
// The helipad sits on a wide low block, front right.
const HELIPAD_BLOCK = { x: 0.3, z: 0.2, w: 0.26, h: 0.12 };

function TowerBlock({ tower, glow }: { tower: Tower; glow: THREE.Material }) {
  const { x, z, w, h, body } = tower;
  const lights = body === GLASS ? glow : WINDOW;
  return (
    <group position={[x, 0, z]}>
      <mesh geometry={G.tower} material={body} position={[0, h / 2, 0]} scale={[w, h, w]} />
      <mesh
        geometry={G.tower}
        material={DARK_CONCRETE}
        position={[0, h + 0.012, 0]}
        scale={[w * 0.9, 0.024, w * 0.9]}
      />
      {tower.strips.map((y) => (
        <mesh
          key={y}
          geometry={G.strip}
          material={lights}
          position={[0, y, 0]}
          scale={[w + 0.006, 0.016, w + 0.006]}
        />
      ))}
    </group>
  );
}

const MAST_HEIGHT = 0.34;

/**
 * L6 Modern: concrete and glass towers banded with lit window strips, the
 * tallest carrying a radio mast with a red beacon, a helipad with a team ring
 * on a low block, and roads crossing a paved hex inside a low concrete kerb.
 */
export function Modern({ accent, glow }: ModelProps) {
  const main = TOWERS[0];
  const pad = HELIPAD_BLOCK;
  return (
    <>
      <mesh geometry={G.plaza} material={DARK_CONCRETE} position={[0, 0.01, 0]} />
      {[0, Math.PI / 3, -Math.PI / 3].map((yaw) => (
        <group key={yaw} rotation={[0, yaw, 0]}>
          <mesh
            geometry={G.lane}
            material={ASPHALT}
            position={[0, 0.023, 0]}
            scale={[WALL_RADIUS * 1.7, 1, 1]}
          />
          <mesh
            geometry={G.laneLine}
            material={ROAD_LINE}
            position={[0, 0.027, 0]}
            scale={[WALL_RADIUS * 1.7, 1, 1]}
          />
        </group>
      ))}
      {WALL_SEGMENTS.map((seg) => (
        <mesh
          key={seg.yaw}
          geometry={G.kerb}
          material={CONCRETE}
          position={[seg.x, 0.02, seg.z]}
          rotation={[0, seg.yaw, 0]}
          scale={[WALL_RADIUS, 1, 1]}
        />
      ))}
      {TOWERS.map((t) => (
        <TowerBlock key={`${t.x}:${t.z}`} tower={t} glow={glow} />
      ))}
      <group position={[main.x, main.h + 0.024, main.z]}>
        <Pole length={MAST_HEIGHT} position={[0, MAST_HEIGHT / 2, 0]} material={STEEL} />
        <Pole
          length={0.08}
          position={[0, MAST_HEIGHT * 0.6, 0]}
          tilt={Math.PI / 2}
          material={STEEL}
        />
        <mesh geometry={G.beacon} material={RED_LIGHT} position={[0, MAST_HEIGHT, 0]} />
      </group>
      <group position={[pad.x, 0, pad.z]}>
        <mesh
          geometry={G.tower}
          material={CONCRETE}
          position={[0, pad.h / 2, 0]}
          scale={[pad.w, pad.h, pad.w]}
        />
        <mesh
          geometry={G.strip}
          material={WINDOW}
          position={[0, 0.07, 0]}
          scale={[pad.w + 0.006, 0.016, pad.w + 0.006]}
        />
        <group position={[0, pad.h, 0]}>
          <mesh geometry={G.helipad} material={ASPHALT} position={[0, 0.006, 0]} />
          <mesh geometry={G.helipadRing} material={accent} position={[0, 0.013, 0]} />
          <mesh geometry={G.hBar} material={WHITE} position={[-0.025, 0.014, 0]} />
          <mesh geometry={G.hBar} material={WHITE} position={[0.025, 0.014, 0]} />
          <mesh geometry={G.hCross} material={WHITE} position={[0, 0.014, 0]} />
        </group>
      </group>
    </>
  );
}

// ------------------------------------------------------------------ Future

const SPIRE_HEIGHT = 1.02;
const RING_SPEED = 0.9; // radians per second
// Domes: angle, ring distance and radius; the big ones at the sides, small ones in front.
const DOMES = [
  { a: 0.35, r: 0.4, size: 0.15 },
  { a: 2.8, r: 0.4, size: 0.15 },
  { a: 1.25, r: 0.42, size: 0.1 },
  { a: 1.95, r: 0.42, size: 0.1 },
];
// Slim needles at the back, either side of the spire.
const NEEDLES = [
  { x: -0.3, z: -0.3, h: 0.5 },
  { x: 0.32, z: -0.26, h: 0.4 },
];
const LIGHT_PADS: [number, number, number][] = [
  [-0.18, 0.016, 0.22],
  [0.18, 0.016, 0.22],
  [0, 0.016, 0.5],
];

/**
 * L7 Future: a white spire with a team collar and a glowing ring turning
 * round it, garden domes under glass, slim light needles and glowing landing
 * pads, all inside a translucent hex energy wall between glowing pylons.
 */
export function Future({ accent, glow }: ModelProps) {
  const field = translucent(glow);
  return (
    <>
      <mesh geometry={G.plaza} material={PANEL} position={[0, 0.01, 0]} />
      {WALL_SEGMENTS.map((seg) => (
        <mesh
          key={seg.yaw}
          geometry={G.wallPanel}
          material={field}
          position={[seg.x, 0.1, seg.z]}
          rotation={[0, seg.yaw, 0]}
          scale={[WALL_RADIUS, 1, 1]}
        />
      ))}
      {WALL_CORNERS.map((c) => (
        <group key={c.a} position={[c.x, 0, c.z]}>
          <mesh geometry={G.pylon} material={HULL} position={[0, 0.1, 0]} />
          <mesh geometry={G.pylonTip} material={glow} position={[0, 0.23, 0]} />
        </group>
      ))}
      <mesh geometry={G.collar} material={accent} position={[0, 0.04, 0]} />
      <mesh
        geometry={G.spire}
        material={HULL}
        position={[0, SPIRE_HEIGHT / 2, 0]}
        scale={[1, SPIRE_HEIGHT, 1]}
      />
      <mesh geometry={G.spireTip} material={glow} position={[0, SPIRE_HEIGHT + 0.02, 0]} />
      <Spin axis="y" speed={RING_SPEED} position={[0, 0.5, 0]}>
        <mesh geometry={G.energyRing} material={glow} rotation={[Math.PI / 2 - 0.35, 0, 0]} />
      </Spin>
      {DOMES.map((d) => (
        <group key={d.a} position={ring(d.r, d.a)} scale={d.size}>
          <mesh geometry={G.domeFloor} material={GARDEN} position={[0, 0.01 / d.size, 0]} />
          <mesh geometry={G.dome} material={DOME} />
          <mesh geometry={G.domeRim} material={accent} />
        </group>
      ))}
      {NEEDLES.map((n) => (
        <group key={n.x} position={[n.x, 0, n.z]}>
          <mesh
            geometry={G.needle}
            material={DARK_PANEL}
            position={[0, n.h / 2, 0]}
            scale={[1, n.h, 1]}
          />
          <mesh
            geometry={G.strip}
            material={glow}
            position={[0, n.h * 0.7, 0]}
            scale={[0.05, 0.02, 0.05]}
          />
          <mesh geometry={G.pylonTip} material={glow} position={[0, n.h + 0.02, 0]} />
        </group>
      ))}
      {LIGHT_PADS.map((p) => (
        <mesh key={p[0] * 10 + p[2]} geometry={G.lightPad} material={glow} position={p} />
      ))}
    </>
  );
}
